import { useEffect, useState } from "react";

interface AlertContact {
  alertContactId: number;
  threshold: number;
  recurrence: number;
}

interface Interval {
  timestamp: string;
  status: "up" | "down";
}

interface LastIncident {
  id: number;
  status: string;
  cause: number;
  reason: string;
  startedAt: string;
  duration: number;
}

interface RegionalData {
  REGION: string[];
}

interface UptimeData {
  id: number;
  friendlyName: string;
  url: string;
  type: string;
  status: "UP" | "DOWN";
  currentStateDuration: number;
  userId: number;
  tags: string[];
  createDateTime: string;
  sslBrand: string | null;
  sslExpiryDateTime: string | null;
  domainExpireDate: string | null;
  checkSSLErrors: boolean;
  sslExpirationReminder: boolean;
  domainExpirationReminder: boolean;
  followRedirections: boolean;
  httpMethodType: string;
  customHttpHeaders: Record<string, string>;
  postValueData: string | null;
  postValueType: string | null;
  port: number | null;
  gracePeriod: number;
  keywordValue: string;
  keywordCaseType: number;
  keywordType: string | null;
  responseTimeThreshold: number;
  config: any;
  maintenanceWindows: any[];
  psps: any[];
  assignedAlertContacts: AlertContact[];
  regionalData: RegionalData;
  avgResponseTime: number | null;
  lastIncident: LastIncident | null;
  intervals: Interval[];
}

interface Props {
  refId: string;
}

const API_URL = process.env.REACT_APP_API_URL;

export const UptimeSection = ({ refId }: Props) => {
  const [data, setData] = useState<UptimeData | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchUptime = async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/api/uptime/${refId}`);
        if (!res.ok) throw new Error("Failed to fetch uptime data");
        const json: UptimeData = await res.json();
        setData(json);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchUptime();
  }, [refId]);

  if (loading || !data) return <div>Loading uptime data...</div>;

  const durationHours = Math.floor(data.currentStateDuration / 3600);
  const durationMinutes = Math.floor((data.currentStateDuration % 3600) / 60);

  return (
    <div className="border rounded-lg p-6 space-y-6 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold">{data.friendlyName}</h2>
          <a
            href={data.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:underline"
          >
            {data.url}
          </a>
        </div>
        <span
          className={`px-3 py-1 rounded text-white font-semibold text-sm ${
            data.status === "UP" ? "bg-green-500" : "bg-red-500"
          }`}
        >
          {data.status.toUpperCase()}
        </span>
      </div>

      {/* Status info */}
      <div className="text-sm text-gray-700 space-y-1">
        <div>
          <strong>Type:</strong> {data.type} ({data.httpMethodType})
        </div>
        <div>
          <strong>Up for:</strong> {durationHours}h {durationMinutes}m
        </div>
        <div>
          <strong>Created:</strong>{" "}
          {new Date(data.createDateTime).toLocaleDateString()}
        </div>
      </div>

      {/* Last incident */}
      {data.lastIncident && (
        <div className="p-3 rounded bg-red-50 border border-red-100 text-sm text-red-700">
          <strong>Last Incident:</strong> {data.lastIncident.reason} —{" "}
          {new Date(data.lastIncident.startedAt).toLocaleString()} (
          {(data.lastIncident.duration / 60).toFixed(0)} minutes)
        </div>
      )}

      {/* SSL / Domain / Performance */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-gray-700">
        <div>
          <strong>SSL Errors:</strong>{" "}
          {data.checkSSLErrors ? "Enabled" : "Disabled"}
        </div>
        <div>
          <strong>Follow Redirects:</strong>{" "}
          {data.followRedirections ? "Yes" : "No"}
        </div>
        <div>
          <strong>Grace Period:</strong> {data.gracePeriod}s
        </div>
        {data.sslExpiryDateTime && (
          <div>
            <strong>SSL Expiry:</strong>{" "}
            {new Date(data.sslExpiryDateTime).toLocaleDateString()}
          </div>
        )}
        {data.domainExpireDate && (
          <div>
            <strong>Domain Expiry:</strong>{" "}
            {new Date(data.domainExpireDate).toLocaleDateString()}
          </div>
        )}
        {data.avgResponseTime !== null && (
          <div>
            <strong>Avg Response Time:</strong> {data.avgResponseTime} ms
          </div>
        )}

        {data.regionalData?.REGION?.length > 0 && (
          <div className="text-sm text-gray-700">
            <strong>Regions:</strong>{" "}
            {data.regionalData.REGION.map((r) => r.toUpperCase()).join(", ")}
          </div>
        )}
      </div>
    </div>
  );
};
